import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FileDropComponent, FileUploadService, GalleryImage, rxResourceFixed } from '@zayan-erp/shared';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import { InputFieldComponent } from '@zayan-erp/shared';
import { DynamicRouteHandlingService } from 'libs/shared/src/lib/services/dynamic-route-handling.service';
import { of, tap, switchMap, map } from 'rxjs';
import { ProductSubcategoriesService } from '../../services/product-subcategories.service';
import { ActivatedRoute, Router } from '@angular/router';
import { GenericDropdownComponent } from '@zayan-erp/shared';
import { selectCategoryListByProductListId, selectProductList } from '@zayan-erp/stores';
import { Store } from '@ngrx/store';
import { AsyncPipe } from '@angular/common';
import * as HomebuildingActions from '@zayan-erp/stores';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { HttpEventType } from '@angular/common/http';

type SubcategoryImageState = GalleryImage & { progress: number | null; s3Key: string; documentId: string };

@Component({
  selector: 'lib-product-subcategories-form',
  imports: [InputFieldComponent, ReactiveFormsModule, GenericDropdownComponent, AsyncPipe, FileDropComponent],
  templateUrl: './product-subcategories-form.component.html'
})
export class ProductSubcategoriesFormComponent {
  #dynamicRouteHandling = inject(DynamicRouteHandlingService);
  #productSubcategoriesService = inject(ProductSubcategoriesService);
  #activatedRoute = inject(ActivatedRoute);
  #router = inject(Router);
  #store = inject(Store);
  #fileUploadService = inject(FileUploadService);
  #destroyRef = inject(DestroyRef);

  selectedSubcategoryId = signal<string | null>(this.#activatedRoute.snapshot.paramMap.get('id') ?? null);

  categoryImage = signal<SubcategoryImageState | null>(null);
  categoryImageDocumentId = signal<string | null>(null);
  existingImageUrl = signal<string | null>(null);

  constructor() {
    this.#dynamicRouteHandling.submitButton$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.onSubmit();
    });
    this.subcategoryForm.get('productListId')?.valueChanges.subscribe(productListId => {
      this.subcategoryForm.get('categoryId')?.setValue(null);
      if (productListId) {
        this.#store.dispatch(HomebuildingActions.loadCategoryListByProductListId({ productListId: productListId }));
      }
      this.categoriesListByProductListId$ = this.#store.select(selectCategoryListByProductListId(productListId!));
    });
  }

  productList$ = this.#store.select(selectProductList);
  categoriesListByProductListId$ = this.#store.select(
    selectCategoryListByProductListId(this.#activatedRoute.snapshot.queryParamMap.get('productListId') ?? '')
  );

  subcategoryForm = new FormGroup({
    code: new FormControl<string | null>(null, Validators.required),
    productListId: new FormControl<string | null>(
      {
        value: this.#activatedRoute.snapshot.queryParamMap.get('productListId') ?? null,
        disabled: !!this.selectedSubcategoryId()
      },
      Validators.required
    ),
    categoryId: new FormControl<string | null>(
      {
        value: this.#activatedRoute.snapshot.queryParamMap.get('categoryId') ?? null,
        disabled: !!this.selectedSubcategoryId()
      },
      Validators.required
    ),
    description: new FormControl<string | null>(null),
    imageId: new FormControl<string | null>(null)
  });

  editRxResource = rxResourceFixed({
    params: () => ({ id: this.selectedSubcategoryId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.id) {
        return this.#productSubcategoriesService.readSubcategoriesById(params.id).pipe(
          tap(subcategory => {
            this.editForm(subcategory);
          })
        );
      }
      return of(null);
    }
  });

  editForm(subcategory: any) {
    this.subcategoryForm.patchValue({
      productListId: subcategory.productListId,
      categoryId: subcategory.categoryId,
      code: subcategory.code,
      description: subcategory.description,
      imageId: subcategory.image?.id ?? null
    });
    this.existingImageUrl.set(subcategory.image?.directUrl ?? null);
  }

  onImageFileDrop(files: NgxFileDropEntry[]) {
    const dropped = files[0];
    if (!dropped?.fileEntry.isFile) return;
    (dropped.fileEntry as FileSystemFileEntry).file((file: File) => {
      this.#setImageAndUpload(file);
    });
  }

  onImageFileChange(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) this.#setImageAndUpload(file);
  }

  #setImageAndUpload(file: File) {
    this.categoryImage.set({
      file,
      fileSrcUrl: URL.createObjectURL(file),
      selected: false,
      progress: null,
      s3Key: '',
      documentId: ''
    });
    this.#uploadImage(file);
  }

  #uploadImage(file: File) {
    this.#fileUploadService
      .getPrivateAWSSignedUrl(file)
      .pipe(
        switchMap(awsData =>
          this.#fileUploadService.uploadFileToS3(awsData).pipe(
            tap((uploadEvent: any) => {
              if (uploadEvent?.type === HttpEventType.UploadProgress && uploadEvent.total) {
                const progress = Math.round((100 * uploadEvent.loaded) / uploadEvent.total);
                this.categoryImage.update(img =>
                  img ? { ...img, progress, s3Key: awsData.s3Key, documentId: awsData.documentId } : img
                );
              }
            }),
            map(() => awsData)
          )
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(awsData => {
        this.categoryImageDocumentId.set(awsData.documentId);
        this.categoryImage.update(img =>
          img ? { ...img, progress: 100, s3Key: awsData.s3Key, documentId: awsData.documentId } : img
        );
      });
  }

  onSubmit() {
    if (this.subcategoryForm.invalid) {
      this.subcategoryForm.markAllAsTouched();
      return;
    }
    const subcategoryData = {
      id: this.selectedSubcategoryId(),
      description: this.subcategoryForm.value.description ?? '',
      categoryId: this.subcategoryForm.value.categoryId ?? '',
      code: this.subcategoryForm.value.code ?? '',
      productListId: this.subcategoryForm.value.productListId ?? '',
      imageId: this.categoryImageDocumentId() ?? undefined
    };
    if (this.selectedSubcategoryId()) {
      this.#productSubcategoriesService.updateSubcategories(subcategoryData).subscribe({
        next: () => {
          this.subcategoryForm.reset();
          this.#router.navigate([this.#activatedRoute.snapshot.data['returnRoute']], {
            queryParamsHandling: 'preserve'
          });
          this.#dynamicRouteHandling.reload.next(!this.#dynamicRouteHandling.reload.value);
          this.#store.dispatch(
            HomebuildingActions.loadSubcategories({
              productListId: subcategoryData.productListId,
              categoryListId: subcategoryData.categoryId,
              force: true
            })
          );
        }
      });
    } else {
      this.#productSubcategoriesService.createSubcategories(subcategoryData).subscribe({
        next: () => {
          this.subcategoryForm.reset();
          this.#router.navigate([this.#activatedRoute.snapshot.data['returnRoute']], {
            queryParamsHandling: 'preserve'
          });
          this.#dynamicRouteHandling.reload.next(!this.#dynamicRouteHandling.reload.value);
          this.#store.dispatch(
            HomebuildingActions.loadSubcategories({
              productListId: subcategoryData.productListId,
              categoryListId: subcategoryData.categoryId,
              force: true
            })
          );
        }
      });
    }
  }
}
